import { createReducer } from '../store.model';

import { getColourGroup } from './functions/get-colour-group.function';
import { getPerformanceHeaderData } from './functions/get-performance-card-header-data.function';
import { performancePillars } from './functions/get-performance-pillars.function';
import { getPeriodCompareDialogData } from './functions/get-period-compare-dialog-data.function';
import { getPeriodPillar } from './functions/get-period-pillar.function';
import { getPopulationCompareData } from './functions/get-population-compare-data.function';
import { CriteriaIdTypeNames } from './models/criteria-type.model';
import { CustomPerformanceDataViewType } from './models/customer-performance-data-view-type.enum';
import { PerformancePillar } from './models/performance-pillar.model';
import { PeriodCompareDialogData } from './models/period-compare-dialog.model';
import {
    ActionType,
    ReloadResultAction,
    RootClearStateAction,
    RootReloadAction,
    SelectMenuItemAction,
    SelectPeriodAction,
    SelectPeriodResultAction,
    SelectPopulationResultAction,
} from './my-performance-card.action';
import {
    defaultState,
    PerformanceCardState,
} from './my-performance-card.state';

const { builder, reducer } = createReducer<ActionType, PerformanceCardState>(
    defaultState(),
);

builder
    .add<RootReloadAction>(
        ActionType.MY_PERFORMANCE_CARD__ROOT__RELOAD,
        (state) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: true,
                    hasData: true,
                },
            };
        },
    )

    .add<ReloadResultAction>(
        ActionType.MY_PERFORMANCE_CARD__ROOT__RELOAD_RESULT,
        (state, action) => {
            const card = action.payload.performanceCard;
            const userName = `${action.payload.user.firstName} ${action.payload.user.lastName}`;

            if (card == null) {
                return {
                    ...state,
                    pending: {
                        ...state.pending,
                        reload: false,
                        hasData: false,
                    },
                };
            }

            const pillarsData: PerformancePillar[] = [];
            const totalPerformance = performancePillars(
                card.total,
                card.component,
            );

            const totalPerformancePillar: PerformancePillar = {
                parentPillar: totalPerformance.filter(
                    (e) => !(e.isChildPillar ?? false),
                )[0],
                childPillars: totalPerformance.filter((e) => e.isChildPillar),
            };

            card.pillars.forEach((pillar) => {
                const pillars = performancePillars(pillar, card.component);
                const pillarData: PerformancePillar = {
                    parentPillar: pillars.filter(
                        (e) => !(e.isChildPillar ?? false),
                    )[0],
                    childPillars: pillars.filter((e) => e.isChildPillar),
                };
                pillarsData.push(pillarData);
            });

            const periodDialogData = getPeriodCompareDialogData(
                action.payload.periodCompare,
            );
            const headerData = getPerformanceHeaderData(card, userName);
            const populationCompareDialogData = getPopulationCompareData(
                card,
                action.payload.populationCompare,
            );

            const periodCompareDialogData: PeriodCompareDialogData = {
                data: periodDialogData,
                selectedPeriodOption: headerData.periodDate,
            };

            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                    hasData: true,
                },
                performanceCard: card,
                totalPerformancePillar,
                pillarsData,
                periodCompareDialogData,
                headerData,
                populationCompareDialogData,
            };
        },
    )

    .add<SelectMenuItemAction>(
        ActionType.MY_PERFORMANCE_CARD_SELECT_MENU_ITEM,
        (state, action) => {
            const { index, menuItemId } = action.payload;

            const totalPerformancePillar = state.totalPerformancePillar;
            const pillarsData = state.pillarsData;

            const targetPillar =
                index != null ? pillarsData[index] : totalPerformancePillar;

            targetPillar.childPillars.forEach((e) => {
                if (e.viewType === menuItemId) {
                    e.isVisible = !(e.isVisible ?? false);
                }
            });

            targetPillar.parentPillar.dropdownContent[0].subMenu.forEach(
                (e) => {
                    if (e.id === menuItemId) {
                        e.isChecked = !e.isChecked;
                    }
                },
            );

            targetPillar.childPillars = targetPillar.childPillars.filter(
                (c) => c.viewType !== CustomPerformanceDataViewType.PERIOD_VIEW,
            );

            return {
                ...state,
                totalPerformancePillar,
                pillarsData,
            };
        },
    );

builder
    .add<SelectPeriodAction>(
        ActionType.MY_PERFORMANCE_CARD_SELECT_PERIOD_COMPARE,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: true,
                },
            };
        },
    )
    .add<SelectPeriodResultAction>(
        ActionType.MY_PERFORMANCE_CARD_SELECT_PERIOD_COMPARE_RESULT,
        (state, action) => {
            if (action.payload.performanceCard == null) {
                return {
                    ...state,
                    pending: {
                        ...state.pending,
                        reload: false,
                    },
                };
            }
            const { selectedDate, pillarIndex, performanceCard } =
                action.payload;

            const totalPerformancePillar = state.totalPerformancePillar;
            const pillarsData = state.pillarsData;

            const targetPillar =
                pillarIndex !== undefined
                    ? pillarsData[pillarIndex]
                    : totalPerformancePillar;

            targetPillar.childPillars.forEach((e) => {
                e.isVisible = false;
            });

            targetPillar.parentPillar.dropdownContent[0].subMenu.forEach(
                (e) => {
                    e.isChecked = false;
                },
            );

            const colourGroups =
                pillarIndex !== undefined
                    ? performanceCard.pillars.find(
                          (e) =>
                              e.name === targetPillar.parentPillar.titleLabel,
                      )?.colourGroups
                    : performanceCard.total.colourGroups;

            const periodPillar = getPeriodPillar(
                selectedDate.options,
                colourGroups,
            );
            targetPillar.childPillars.push(periodPillar);

            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                },
                totalPerformancePillar,
                pillarsData,
            };
        },
    );

builder.add<SelectPopulationResultAction>(
    ActionType.MY_PERFORMANCE_CARD_SELECT_POPULATION_COMPARE_RESULT,
    (state, action) => {
        const {
            metrics: populationMetrics,
            pillarIndex,
            selectedData,
        } = action.payload;

        if (populationMetrics == null || populationMetrics.length === 0) {
            return {
                ...state,
            };
        }

        const totalPerformancePillar = state.totalPerformancePillar;
        const pillarsData = state.pillarsData;
        const pillarName = CriteriaIdTypeNames[Object.keys(selectedData)[0]];
        const colourGroup = getColourGroup(populationMetrics);

        const periodPillar = getPeriodPillar(pillarName, colourGroup);

        if (pillarIndex === undefined) {
            totalPerformancePillar.childPillars.push(periodPillar);
        } else {
            pillarsData[pillarIndex].childPillars.push(periodPillar);
        }

        return {
            ...state,
            totalPerformancePillar,
            pillarsData,
        };
    },
);

builder.add<RootClearStateAction>(
    ActionType.MY_PERFORMANCE_CARD_ROOT__CLEAR_STATE,
    () => {
        return defaultState();
    },
);

export const performanceCardReducer = reducer;
